import { useState } from 'react';
import { AlertTriangle, CalendarDays, TrendingDown, Sparkles, DollarSign, ChevronRight, Truck } from 'lucide-react';
import { GlassCard } from './GlassCard';
import GlassTable from './GlassTable';
import Drawer from './Drawer';
import { formatINR } from '../lib/formatINR';

/* ── helpers ── */
function fmtDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function fmtCover(days) {
  const n = Number(days ?? 0);
  return `~${Math.round(Math.abs(n))}d cover`;
}

/* ────────────────────────────────────────────────────────────
   Phase 12 · Purchase Negotiator Sections
   Renders BELOW the Slice-C insight cards on /managers/purchase
   ──────────────────────────────────────────────────────────── */
export default function PurchaseNegotiatorSections({
  stockouts, smartOrders, vendorComparison, onReorder, approved,
}) {
  const [vendorDrawer, setVendorDrawer] = useState(null);

  const hasStockouts = stockouts.length > 0;
  const hasSmartOrders = smartOrders.length > 0;

  return (
    <>
      {/* ═══ 1. Stock-Out Predictions ═══ */}
      {hasStockouts && (
        <div className="mt-6" data-testid="p12-stockouts-section">
          <h3 className="flex items-center gap-2 text-white/60 text-xs font-bold uppercase tracking-wider mb-3">
            <AlertTriangle size={14} className="text-red-400" />
            Stock-Out Predictions
          </h3>
          <div className="space-y-2">
            {stockouts.slice(0, 6).map((p) => (
              <GlassCard
                dark
                key={p.product_id}
                className="p-4 flex items-center gap-3 rounded-[32px]"
                testId={`p12-web-stockout-${p.product_id}`}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-white font-bold text-sm truncate">{p.product_name}</p>
                  <p className="text-white/40 text-xs mt-0.5 tabular-nums">
                    {fmtCover(p.days_until_stockout ?? p.days_of_cover)}
                    {p.predicted_stockout_date ? ` · stockout ${fmtDate(p.predicted_stockout_date)}` : ''}
                  </p>
                </div>
                <span className="flex-shrink-0 px-3 py-1.5 rounded-full bg-red-500/15 text-red-400 text-xs font-extrabold tabular-nums">
                  {p.suggested_reorder_qty ?? '—'}
                </span>
              </GlassCard>
            ))}
          </div>
        </div>
      )}

      {/* ═══ 2. Seasonal Smart Ordering ═══ */}
      {hasSmartOrders && (
        <div className="mt-6" data-testid="p12-smart-section">
          <h3 className="flex items-center gap-2 text-white/60 text-xs font-bold uppercase tracking-wider mb-3">
            <CalendarDays size={14} className="text-amber-400" />
            Seasonal Smart Ordering
          </h3>

          {/* PO created banner */}
          {approved?.kind === 'restock_po' && (
            <div
              className="mb-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-500/25 text-emerald-400 text-xs font-bold"
              data-testid="p12-po-created-chip"
            >
              <Sparkles size={12} /> PO created · {approved.id?.slice(0, 8)}
            </div>
          )}

          <div className="space-y-3">
            {smartOrders.slice(0, 8).map((o) => (
              <SmartOrderCard
                key={o.product_id}
                order={o}
                onReorder={onReorder}
              />
            ))}
          </div>
        </div>
      )}

      {/* ═══ 3. Cheapest Internal Supplier ═══ */}
      <div className="mt-6" data-testid="p12-vendor-section">
        <h3 className="flex items-center gap-2 text-white/60 text-xs font-bold uppercase tracking-wider mb-3">
          <DollarSign size={14} className="text-emerald-400" />
          Cheapest Internal Supplier
        </h3>
        {vendorComparison.length === 0 ? (
          <GlassCard dark className="p-5 flex items-center gap-4 rounded-[32px]" testId="p12-vendor-empty">
            <div className="w-10 h-10 rounded-full bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center flex-shrink-0">
              <Truck size={18} className="text-emerald-400/60" />
            </div>
            <p className="text-white/35 text-sm leading-relaxed">
              No multi-vendor SKUs in the last 180 days. Add a second supplier for any product to unlock cost comparisons.
            </p>
          </GlassCard>
        ) : (
          <div className="space-y-2">
            {vendorComparison.slice(0, 8).map((v) => {
              const savingsPct =
                v.average_unit_cost_rupees > 0 && v.cheapest_vendor
                  ? Math.round(
                      ((v.average_unit_cost_rupees - v.cheapest_vendor.unit_cost_rupees) /
                        v.average_unit_cost_rupees) *
                        100,
                    )
                  : 0;

              return (
                <GlassCard
                  dark
                  key={v.product_id}
                  className="p-4 flex items-center gap-3 cursor-pointer hover:bg-white/[0.06] transition-colors rounded-[32px]"
                  testId={`p12-web-vendor-row-${v.product_id}`}
                  onClick={() => setVendorDrawer(v)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-bold text-sm truncate">{v.product_name}</p>
                    {v.cheapest_vendor && (
                      <p className="text-white/40 text-xs mt-0.5 tabular-nums">
                        {formatINR(v.cheapest_vendor.unit_cost_rupees)} from {v.cheapest_vendor.vendor_name}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {savingsPct > 0 && (
                      <span className="px-2.5 py-1 rounded-full bg-emerald-500/15 text-emerald-400 text-[11px] font-bold tabular-nums border border-emerald-500/25">
                        vs avg {formatINR(v.average_unit_cost_rupees)} · -{savingsPct}%
                      </span>
                    )}
                    <ChevronRight size={16} className="text-white/20" />
                  </div>
                </GlassCard>
              );
            })}
          </div>
        )}
      </div>

      {/* ═══ Vendor Comparison Drawer ═══ */}
      <Drawer
        open={!!vendorDrawer}
        onClose={() => setVendorDrawer(null)}
        title={`Vendor Comparison — ${vendorDrawer?.product_name || ''}`}
      >
        {vendorDrawer && (
          <VendorDrawerBody
            item={vendorDrawer}
            onClose={() => setVendorDrawer(null)}
          />
        )}
      </Drawer>
    </>
  );
}

/* ── Smart Order Card ── */
function SmartOrderCard({ order: o, onReorder }) {
  const holidays = o.matching_holidays || [];
  const trend = o.market_trend;

  return (
    <GlassCard
      dark
      className="p-4 space-y-3 rounded-[32px]"
      testId={`p12-web-smart-${o.product_id}`}
    >
      {/* Row 1: Name + Qty */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <p className="text-white font-extrabold text-sm truncate">{o.product_name}</p>

          {/* Chip stack */}
          <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {holidays.map((h) => (
              <span
                key={h.name}
                className="px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-500 text-[10px] font-bold"
              >
                {h.name} +{Math.round(o.holiday_lift_pct || 0)}%
              </span>
            ))}
            {trend === 'opportunity' && (
              <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[10px] font-bold">
                Opportunity +10%
              </span>
            )}
            {trend === 'competitor_price_drop' && (
              <span className="px-2 py-0.5 rounded-full bg-red-500/15 text-red-400 text-[10px] font-bold flex items-center gap-0.5">
                <TrendingDown size={10} /> Rival ↓ price -10%
              </span>
            )}
          </div>
        </div>

        <div className="text-right flex-shrink-0">
          <p className="text-white font-extrabold text-2xl tabular-nums">{o.suggested_qty}</p>
          <p className="text-white/40 text-[10px] font-medium">units</p>
        </div>
      </div>

      {/* Row 2: Rationale */}
      {o.rationale && (
        <p className="text-white/50 text-xs leading-relaxed line-clamp-2">{o.rationale}</p>
      )}

      {/* Row 3: Prepare Reorder */}
      <button
        data-testid={`p12-web-reorder-btn-${o.product_id}`}
        onClick={() => onReorder(o)}
        className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-navy-900 text-white text-xs font-bold hover:bg-navy-800 transition-colors border border-white/10"
      >
        Prepare Reorder{o.cheapest_vendor ? ` · ${o.cheapest_vendor.vendor_name}` : ''}
      </button>
    </GlassCard>
  );
}

/* ── Vendor Drawer Body ── */
function VendorDrawerBody({ item, onClose }) {
  const cheapest = item.cheapest_vendor;
  const alternates = item.alternate_vendors || [];

  const columns = [
    {
      key: 'vendor_name',
      label: 'Name',
      render: (v, row) => (
        <span className="text-white font-semibold flex items-center gap-2">
          {v}
          {row._isCheapest && (
            <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 text-[9px] font-bold uppercase">
              Cheapest
            </span>
          )}
        </span>
      ),
    },
    {
      key: 'unit_cost_rupees',
      label: 'Unit Cost',
      align: 'right',
      render: (v) => <span className="tabular-nums">{formatINR(v)}</span>,
    },
    {
      key: 'savings_vs_cheapest_pct',
      label: 'vs Cheapest',
      align: 'right',
      render: (v, row) =>
        row._isCheapest ? (
          <span className="text-emerald-400 font-bold text-xs">—</span>
        ) : (
          <span className="tabular-nums text-white/60">+{v ?? 0}%</span>
        ),
    },
    {
      key: 'last_purchased_on',
      label: 'Last Purchased',
      align: 'right',
      render: (v) => <span className="text-white/50 text-xs">{v || '—'}</span>,
    },
  ];

  const rows = [];
  if (cheapest) {
    rows.push({
      ...cheapest,
      _isCheapest: true,
      savings_vs_cheapest_pct: 0,
    });
  }
  alternates.forEach((a) => rows.push({ ...a, _isCheapest: false }));

  return (
    <div className="space-y-5" data-testid="p12-web-vendor-drawer">
      <div className="glass-card p-4">
        <p className="text-white/40 text-[10px] font-bold uppercase tracking-wider mb-1">Average Unit Cost</p>
        <p className="text-white font-extrabold text-xl tabular-nums">{formatINR(item.average_unit_cost_rupees)}</p>
        {item.savings_opportunity_rupees > 0 && (
          <p className="text-emerald-400 text-xs mt-1 tabular-nums">
            Save {formatINR(item.savings_opportunity_rupees)}/unit going cheapest
          </p>
        )}
      </div>

      <GlassTable
        columns={columns}
        data={rows}
        testId="p12-vendor-table"
      />

      <button
        data-testid="p12-web-vendor-drawer-close"
        onClick={onClose}
        className="w-full h-11 rounded-xl bg-white/5 border border-white/10 text-white/60 font-bold text-sm hover:bg-white/10 transition-colors"
      >
        Close
      </button>
    </div>
  );
}
